/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useEffect } from "react";

import { LoadingOutlined } from "@ant-design/icons";
import { Spin, Table } from "antd";

import styles from "./styles.module.css";
import { applyMargin, camelcaseToWord } from "../../../../helper/service";

const TravelComponent = ({
  rawData,
  distance,
  teamData,
  margin,
  errorMsg,
  loading,
  updateTravelTotal,
  calculateCreditCardCharge,
  calculateCommission,
  haveSoundCompany,
}) => {
  const [newData, setNewData] = useState([]);
  const [performersCount, setPerformersCount] = useState(0);
  const [totalMembers, setTotalMembers] = useState(0);

  useEffect(() => {
    if (teamData === undefined) {
      setPerformersCount(0);
      setTotalMembers(0);
    } else {
      const count = teamData
        .filter((item) => item.noTravel === false)
        .reduce((prev, cur) => {
          if (cur.default > 0) {
            return prev + cur.default;
          }
          return prev;
        }, 0);
      setPerformersCount(count);

      let teamCount = teamData.reduce((prev, cur) => {
        if (cur.default > 0) {
          return prev + cur.default;
        }
        return prev;
      }, 0);
      if (haveSoundCompany) {
        teamCount += 1;
      }
      setTotalMembers(teamCount);
    }

    updateTravelData(rawData);
  }, [teamData, margin, rawData]);

  useEffect(() => {
    updateTravelData(rawData);
  }, [performersCount, totalMembers]);

  const getTravelMarginRate = (key, value) => {
    let price = 0;
    switch (key) {
      case "travelRate":
        // calc flight margin price
        const flightRate = performersCount * rawData["flightFare"];
        const fightMarginRate = applyMargin(
          flightRate,
          rawData["marginPercentage"]
        );
        let flightMargin = fightMarginRate - flightRate;
        if (isNaN(flightMargin)) flightMargin = 0;

        // calc hotel margin price for 2 days
        const hotelRate = 2 * rawData["hotelFare"] * totalMembers;
        const hotelMarginRate = applyMargin(
          hotelRate,
          rawData["marginPercentage"]
        );
        let hotelMargin = hotelMarginRate - hotelRate;
        if (isNaN(hotelMargin)) hotelMargin = 0;

        // sum of flight and hotel margin
        price = flightMargin + hotelMargin;
        if (isNaN(price)) price = 0;
        break;
      case "flightFare":
        price = performersCount * value;
        break;
      case "hotelFare":
        const twoDaysFare = value * 2;
        price = totalMembers * twoDaysFare;
        break;
      case "perDiem":
        price = totalMembers * value;
        break;
      case "bandMeal":
        price = applyMargin(totalMembers * value, margin);
        break;
      case "baggageFee":
        price = performersCount * value;
        break;
      default:
        price = applyMargin(value, margin);
        break;
    }
    return price;
  };

  const updateTravelData = (arr) => {
    console.log('Raw travel data received:', JSON.stringify(arr, null, 2));
    if (haveSoundCompany === false && arr && arr.hasOwnProperty("equipmentTransportationCost")) {
      delete arr.equipmentTransportationCost;
    }
    
    if (!arr) {
      console.error('No travel data received');
      return;
    }

    const arrNew = Object.keys(arr)
      .filter((key) => key !== "marginPercentage" && key !== "travelRate")
      .map((key, index) => {
        const rate = arr[key];
        console.log(`Processing ${key}:`, { rate });
        
        const marginRate = getTravelMarginRate(key, rate);
        console.log(`Margin rate for ${key}:`, marginRate);
        
        const ccCharge = calculateCreditCardCharge(marginRate, undefined) ?? 0;
        console.log(`CC charge for ${key}:`, ccCharge);
        
        const commissionPrice = calculateCommission(marginRate + ccCharge, undefined);
        console.log(`Commission for ${key}:`, commissionPrice);
        
        const marginRateWithCC = Math.round(marginRate + ccCharge + commissionPrice);
        console.log(`Final marginRateWithCC for ${key}:`, marginRateWithCC);
        
        return {
          id: index,
          name: key,
          rate,
          marginRate,
          marginRateWithCC
        };
      });
      
    console.log('Processed travel data:', JSON.stringify(arrNew, null, 2));
    setNewData(arrNew);
    updateTravelTotal(arrNew);
  };

  const columns = [
    {
      title: "Travel",
      dataIndex: "name",
      key: "name",
      render: (text) => <span>{camelcaseToWord(text)}</span>,
    },
    {
      title: "Price",
      dataIndex: "marginRateWithCC",
      key: "marginRateWithCC",
      render: (rate) => <span>${rate ? Math.round(rate) : 0}</span>,
    },
  ];

  if (errorMsg) {
    return (
      <div className="d-flex justify-content-center align-items-center h-100 mt-2">
        <p className="band-config-table-errMsg">{errorMsg}</p>
      </div>
    );
  }

  if (!loading && errorMsg === "" && teamData === undefined)
    return (
      <div className="d-flex justify-content-center align-items-center h-100 mt-2">
        <p className="band-config-table-errMsg">
          Something went wrong! Please try again later.
        </p>
      </div>
    );

  if (loading && newData.length === 0)
    return (
      <div className="d-flex justify-content-center align-items-center">
        <Spin
          spinning={true}
          style={{ height: "300px", backgroundColor: "transparent" }}
          indicator={
            <LoadingOutlined style={{ fontSize: 32, color: "#29a6ed" }} spin />
          }
        />
      </div>
    );

  return (
    <>
      {errorMsg === "" && newData.length > 0 && distance >= 90 ? (
        <div className={`row ${styles.travel_content_tab}`}>
          <Table
            className={`col-lg-8 ${styles.band_config_travel_table}`}
            dataSource={newData}
            columns={columns}
            pagination={false}
          />
          <div className="col-lg-4 hidden-md">
            <div className={styles.extra_img}>
              <img alt="Extras" src={"/assets/images/travel_circle.png"} />
            </div>
          </div>
        </div>
      ) : (
        <div className="d-flex justify-content-center align-items-center h-100 mt-2">
          {distance < 90 ? (
            <p className="band-config-table-errMsg">
              Travel fees do not apply to your event
            </p>
          ) : (
            <Spin
              spinning={true}
              style={{ height: "300px", backgroundColor: "transparent" }}
              indicator={
                <LoadingOutlined
                  style={{ fontSize: 32, color: "#29a6ed" }}
                  spin
                />
              }
            />
          )}
        </div>
      )}
    </>
  );
};

export default TravelComponent;
